import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  contentChildren,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import { ChildActivationEnd, NavigationEnd, QueryParamsHandling, Router } from '@angular/router';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { startWith } from 'rxjs/operators';
import { TabStyle, V2TabsComponent } from '../v2-tabs.component';
import { V2TabDirective } from '../tab/v2-tab.directive';

@Component({
  selector: 'coin-v2-routed-tabs',
  templateUrl: './routed-tabs.component.html',
  styleUrl: './routed-tabs.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class RoutedTabsComponent {
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  public readonly type = input.required<TabStyle>();
  public readonly queryParamsHandling = input<QueryParamsHandling>('');
  public readonly defaultToFirstTab = input(false);
  public tabSelected = output<V2TabDirective>();

  private readonly v2TabsComponent = viewChild(V2TabsComponent);
  protected readonly tabs = contentChildren(V2TabDirective);

  private currentUrl = signal('');
  private routedTab = computed(() =>
    this.v2TabsComponent()
      .tabs()
      ?.find(tab => tab.matchesUrl(this.currentUrl()))
  );

  constructor() {
    this.listenToRouteChanges();
    this.autoSelectRoutedTab();
  }

  protected async navigateOnTabSelect(tab: V2TabDirective): Promise<void> {
    if (tab.matchesUrl(this.router.url)) return;

    const route = tab.route();
    this.currentUrl.set(route);
    if (route) await this.router.navigate([route], { queryParamsHandling: this.queryParamsHandling() });

    this.tabSelected.emit(tab);
  }

  private listenToRouteChanges(): void {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd | ChildActivationEnd =>
            event instanceof NavigationEnd || event instanceof ChildActivationEnd
        ),
        startWith(null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.currentUrl.set(this.router.url));
  }

  private autoSelectRoutedTab(): void {
    effect(() => {
      if (!this.v2TabsComponent()) return;

      if (this.v2TabsComponent().isTabSelected(this.routedTab())) return;

      this.v2TabsComponent().setSelectedTab(this.routedTab());
    });
  }
}
